import React, { FC, FormEvent, useEffect, useRef, useState } from "react";

import {
  TwoStepCheck,
  TwoStepPass,
  TwoStepVerifyMethod,
  useSendTwoStepCodeMutation,
  useVerifyTwoStepMutation,
} from "../../slices/TwoStepSlice";
import {
  deviceLabel,
  isCompleteCode,
  twoStepErrorMessage,
} from "./twoStep.utils";
import {
  CodeInput,
  ShieldIcon,
  TsAlert,
  TsHeading,
  tsLink,
  tsPrimary,
  TwoStepFrame,
} from "./TwoStepUi";

const TEXT: Record<TwoStepVerifyMethod, string> = {
  app: "Open your authenticator app and enter the 6-digit code for ProCharted.",
  email: "Enter the 6-digit code we emailed you. It works for 10 minutes.",
  recovery:
    "Enter one of the recovery codes you saved when you set up two-step sign-in. Each code works once.",
};

/**
 * The code screen after the Microsoft sign-in: authenticator code or "Email me
 * a code", "Remember this computer for 30 days", "Use a recovery code", Sign out.
 */
export const TwoStepCodeScreen: FC<{
  check: TwoStepCheck;
  onPassed: (pass: TwoStepPass, remembered: boolean) => void;
  onSignOut: () => void;
  autoSendEmail?: boolean;
}> = ({ check, onPassed, onSignOut, autoSendEmail = true }) => {
  const startMethod: TwoStepVerifyMethod =
    check.method === "email" ? "email" : "app";
  const [method, setMethod] = useState<TwoStepVerifyMethod>(startMethod);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sendCode, sendState] = useSendTwoStepCodeMutation();
  const [verify, verifyState] = useVerifyTwoStepMutation();
  const sentOnce = useRef(false);

  const emailMe = async (switchTo = true) => {
    setError(null);
    const result = await sendCode({ purpose: "signin" });
    if ("error" in result) {
      setNotice(null);
      return setError(twoStepErrorMessage(result.error));
    }
    if (switchTo) {
      setMethod("email");
      setCode("");
    }
    setNotice(`We sent a code to ${result.data.sentTo}.`);
  };

  useEffect(() => {
    // Email users get their code straight away (once per screen).
    if (autoSendEmail && startMethod === "email" && !sentOnce.current) {
      sentOnce.current = true;
      void emailMe(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recovery = method === "recovery";
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!isCompleteCode(code, recovery))
      return setError(
        recovery ? "Enter the whole recovery code." : "Enter the 6-digit code.",
      );
    setError(null);
    const result = await verify({
      code,
      method,
      rememberDevice: remember,
      deviceLabel: deviceLabel(navigator.userAgent),
    });
    if ("error" in result) return setError(twoStepErrorMessage(result.error));
    onPassed(result.data, remember);
  };

  const switchMethod = (next: TwoStepVerifyMethod) => {
    setMethod(next);
    setCode("");
    setError(null);
    setNotice(null);
  };

  return (
    <TwoStepFrame
      footer={
        <>
          <ShieldIcon size={14} />
          <span>Two-step sign-in keeps your firm&apos;s client data safe.</span>
        </>
      }>
      <TsHeading
        title={recovery ? "Use a recovery code" : "Two-step sign-in"}
        text={TEXT[method]}
      />
      <form className={"flex flex-col gap-4"} onSubmit={submit}>
        {notice && <TsAlert tone={"info"}>{notice}</TsAlert>}
        <CodeInput
          key={method}
          value={code}
          onChange={setCode}
          recovery={recovery}
          autoFocus={true}
        />
        <label
          className={
            "flex cursor-pointer items-center gap-2.5 text-[13.5px] font-semibold text-[#3A4256]"
          }>
          <input
            type={"checkbox"}
            checked={remember}
            onChange={(event) => setRemember(event.target.checked)}
            className={"h-4 w-4 cursor-pointer accent-[#6B4EFF]"}
          />
          Remember this computer for 30 days
        </label>
        {error && <TsAlert>{error}</TsAlert>}
        <button
          type={"submit"}
          className={tsPrimary}
          disabled={verifyState.isLoading}>
          {verifyState.isLoading ? "Checking…" : "Continue"}
        </button>
      </form>
      <div
        className={
          "flex flex-col gap-2.5 border-t border-[#EDEFF3] pt-4 text-[13px]"
        }>
        {method !== "email" && (
          <button
            type={"button"}
            className={`${tsLink} self-start`}
            onClick={() => void emailMe()}
            disabled={sendState.isLoading}>
            {sendState.isLoading ? "Sending…" : "Email me a code"}
          </button>
        )}
        {method === "email" && (
          <button
            type={"button"}
            className={`${tsLink} self-start`}
            onClick={() => void emailMe(false)}
            disabled={sendState.isLoading}>
            {sendState.isLoading ? "Sending…" : "Send a new code"}
          </button>
        )}
        {method !== "app" && check.method === "app" && (
          <button
            type={"button"}
            className={`${tsLink} self-start`}
            onClick={() => switchMethod("app")}>
            Use my authenticator app
          </button>
        )}
        {!recovery && (
          <button
            type={"button"}
            className={`${tsLink} self-start`}
            onClick={() => switchMethod("recovery")}>
            Use a recovery code
          </button>
        )}
        {recovery && (
          <button
            type={"button"}
            className={`${tsLink} self-start`}
            onClick={() => switchMethod(startMethod)}>
            Back to the code
          </button>
        )}
        <span className={"text-[12.5px] leading-[1.5] text-[#8A90A2]"}>
          Lost your phone and your recovery codes? Ask your firm&apos;s admin to
          reset your two-step sign-in (Settings &gt; Team).
        </span>
        <button
          type={"button"}
          className={
            "self-start border-0 bg-transparent p-0 text-[13px] font-bold text-[#5B6678] hover:text-[#1F2433]"
          }
          onClick={onSignOut}>
          Sign out
        </button>
      </div>
    </TwoStepFrame>
  );
};

/** When the firm requires it and the person hasn't set it up yet. */
export const TwoStepRequiredSetupScreen: FC<{
  check: TwoStepCheck;
  onSignOut: () => void;
  setup: React.ReactNode;
}> = ({ check, onSignOut, setup }) => {
  const firms = check.requiredBy.length
    ? check.requiredBy.join(", ")
    : "Your firm";
  return (
    <TwoStepFrame
      wide={true}
      footer={
        <>
          <ShieldIcon size={14} />
          <span>Two-step sign-in keeps your firm&apos;s client data safe.</span>
        </>
      }>
      <TsHeading
        title={"Set up two-step sign-in"}
        text={
          <>
            <b>{firms}</b> requires two-step sign-in. After your password,
            ProCharted will ask for a 6-digit code. It takes about a minute to
            set up.
          </>
        }
      />
      {setup}
      <div className={"border-t border-[#EDEFF3] pt-4"}>
        <button
          type={"button"}
          className={
            "border-0 bg-transparent p-0 text-[13px] font-bold text-[#5B6678] hover:text-[#1F2433]"
          }
          onClick={onSignOut}>
          Sign out
        </button>
      </div>
    </TwoStepFrame>
  );
};
